import { useEffect } from 'react'
import type { RefObject } from 'react'

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

/**
 * Focus-trap for a non-modal, portaled popover (a `role="dialog"` panel
 * anchored to a trigger, as opposed to `Modal.tsx`'s full-screen backdrop
 * dialog — this hook is the SAME trap-Tab/restore-focus behavior, factored
 * out so a trigger+panel popover can get it without pulling in Modal's
 * backdrop/portal chrome it doesn't want).
 *
 * While `open`: moves focus into the panel's first focusable element,
 * traps `Tab`/`Shift+Tab` inside it, and restores focus once `open` goes
 * false (covers Escape — handled by the caller's own close logic, e.g.
 * `useAnchoredPopover` — and any other close path, since this only depends
 * on `open`).
 *
 * Restores to `returnFocusRef` when given — the popover's own anchor/
 * trigger, so focus predictably lands back exactly there regardless of
 * whether the trigger itself ever received DOM focus on the click/keypress
 * that opened the popover (a real pointer click doesn't always focus a
 * button the way a keyboard activation does). Falls back to "whatever had
 * focus before opening" (Modal.tsx's behavior) when omitted.
 */
export function useFocusTrap(
  panelRef: RefObject<HTMLElement | null>,
  open: boolean,
  returnFocusRef?: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    if (!open) return
    const previouslyFocused = document.activeElement as HTMLElement | null
    const raf = requestAnimationFrame(() => {
      panelRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)?.focus()
    })

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Tab' || !panelRef.current) return
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last?.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first?.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      cancelAnimationFrame(raf)
      document.removeEventListener('keydown', handleKeyDown)
      ;(returnFocusRef?.current ?? previouslyFocused)?.focus()
    }
  }, [open, panelRef, returnFocusRef])
}
